import { useTranslation } from '@multaqa/i18n'
import { Tabs, TabsList, TabsTrigger } from '@multaqa/ui/components/ui/tabs'
import { useEffectiveGrants } from '@/lib/me'
import type { ProductResource } from '../api'

export const productDetailTabs = [
  'overview',
  'variants',
  'purchases',
  'sales',
  'bundle',
  'suppliers',
  'photos',
  'serials',
] as const

export type ProductDetailTab = (typeof productDetailTabs)[number]

export function isProductDetailTab(value: unknown): value is ProductDetailTab {
  return productDetailTabs.some((tab) => tab === value)
}

/**
 * Product detail tabs retain their domain-specific set while sharing the UI
 * package's selection, keyboard, and overflow-cue behavior.
 */
/** A linked Variants tab opens Overview for a reader who may not load variants. */
export function visibleProductTab(
  requested: ProductDetailTab,
  canViewVariants: boolean,
): ProductDetailTab {
  return requested === 'variants' && !canViewVariants ? 'overview' : requested
}

export function ProductDetailTabs({
  activeTab,
  setActiveTab,
  productType,
  trackingMode,
}: {
  activeTab: ProductDetailTab
  setActiveTab: (tab: ProductDetailTab) => void
  productType: ProductResource['type']
  /** WP11: a product tracked by serial number lists its units. */
  trackingMode?: string
}) {
  const { t } = useTranslation()
  // The variants endpoint needs product_variant.view, which product.view alone does not give.
  const canViewVariants = useEffectiveGrants().can('product_variant.view')

  return (
    <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ProductDetailTab)}>
      <TabsList>
        {(
          [
            ['overview', t('product.details')],
            ...(canViewVariants
              ? ([['variants', t('products:variants')]] as Array<[ProductDetailTab, string]>)
              : []),
            ['purchases', t('products:purchasesTab')],
            ['sales', t('products:salesTab')],
            ['suppliers', t('products:suppliersTab.title')],
            ['photos', t('products:photos.title')],
            ...(trackingMode === 'serial'
              ? ([['serials', t('products:serialsTab.title')]] as Array<[ProductDetailTab, string]>)
              : []),
            ...(productType === 'bundle' ? [['bundle', t('products:bundleTitle')]] : []),
          ] as Array<[ProductDetailTab, string]>
        ).map(([value, label]) => (
          <TabsTrigger key={value} value={value}>
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}
